import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import type { Classes } from 'jss';

import type { FormButtonSettings } from '@components/sp-form-button-settings';
import { EButtonStyleType, ESize } from '@common/enums';
import type { ShopCartWidgetData } from '@web/widgets/shop-cart/type/shop-cart-widget.type';
import type { ProductCard } from '@common/types/product-card.type';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';

@Component({
    selector: 'web-shop-cart-mini-cart-preview',
    templateUrl: './shop-cart-mini-cart-preview.component.html',
    styleUrls: ['./shop-cart-mini-cart-preview.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DecimalPipe, TranslateModule],
})
export class ShopCartMiniCartPreviewComponent {
    @Input() public data: ShopCartWidgetData;
    @Input() public classes: Classes;
    @Input() public currency: string = '';

    private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    public get items(): ProductCard[] {
        return this.data?.items ?? [];
    }

    public get buttonSettings(): FormButtonSettings {
        return this.data?.miniCartSettings?.buttonSettings;
    }

    public getItemTotal(item: ProductCard): number {
        return Number(item.price) * Number(item.quantity || 1);
    }

    public getTotalPrice(): number {
        return this.items.reduce((sum, item) => sum + this.getItemTotal(item), 0);
    }

    public getButtonStyles(): string {
        const settings = this.buttonSettings;
        if (!settings) return DEFAULT_BUTTON_CLASS;

        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[settings.size ?? ESize.M]}`;

        if (settings.styleType === EButtonStyleType.custom) {
            return `${styles} ${this.classes?.miniCartButtonStyles ?? ''}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[settings.styleType]} ${this.classes?.miniCartButtonStyles ?? ''}`;
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }
}
